import React, { useEffect, useMemo, useState } from 'react';
import '../css/accounting.css';
import TransactionModal from './TransactionModal';
import {
  getHiddenForecastAccounts,
  normalizeForecastAccountName,
  setHiddenForecastAccounts
} from '../utils/forecastVisibility';

const formatCurrency = (value) => {
  const number = Number(value || 0);
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(number);
};

const normalizeStatementStartDay = (value, fallback = 1) => {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) {
    return fallback;
  }
  return Math.max(1, Math.min(31, parsed));
};

const formatMonthKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const resolveStatementMonthForDate = (date, statementStartDay = 1) => {
  const normalizedStartDay = normalizeStatementStartDay(statementStartDay, 1);
  const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const effectiveStartDay = Math.min(normalizedStartDay, lastDayOfMonth);

  if (date.getDate() >= effectiveStartDay) {
    return formatMonthKey(new Date(date.getFullYear(), date.getMonth() + 1, 1));
  }

  return formatMonthKey(date);
};

const getCurrentCalendarMonthKey = () => formatMonthKey(new Date());

function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [credits, setCredits] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [isEditExpenseModalOpen, setIsEditExpenseModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const [entryType, setEntryType] = useState('credit');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [creditId, setCreditId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [statementMonth, setStatementMonth] = useState(getCurrentCalendarMonthKey());
  const [filterMonth, setFilterMonth] = useState(getCurrentCalendarMonthKey());
  const [filterType, setFilterType] = useState('all');
  const [statusMessage, setStatusMessage] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importYear, setImportYear] = useState(new Date().getFullYear());
  const [importEntryType, setImportEntryType] = useState('credit');
  const [importStatus, setImportStatus] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState('Processing...');
  const [importTotal, setImportTotal] = useState(0);
  const [importDetails, setImportDetails] = useState([]);
  const [deleteYear, setDeleteYear] = useState(new Date().getFullYear());
  const [deleteMonth, setDeleteMonth] = useState(new Date().getMonth() + 1);
  const [deleteStatus, setDeleteStatus] = useState('');
  const [hiddenForecastAccounts, setHiddenForecastAccountsState] = useState([]);
  const [hasInitializedCreditCycleMonth, setHasInitializedCreditCycleMonth] = useState(false);

  const user = localStorage.getItem('loggedInUsername');

  const getPreferredCreditId = (creditsList) => {
    const preferred = creditsList.find((credit) => String(credit.name || '').trim().toLowerCase() === 'barclaycard');
    if (preferred) {
      return String(preferred.id);
    }
    return creditsList.length > 0 ? String(creditsList[0].id) : '';
  };

  const sortAccountsByPreference = (accountsList) => {
    const rank = (name) => {
      const normalized = String(name || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');

      if (normalized.includes('current')) {
        return 0;
      }
      if (normalized === 'rds' || normalized.includes('rainyday') || normalized.includes('rainydaysaver')) {
        return 3;
      }
      if (normalized.includes('regular') || normalized === 'saver' || normalized.includes('regularsaver')) {
        return 1;
      }
      if (normalized.includes('investmentisa') || normalized.includes('investment')) {
        return 2;
      }

      return 4;
    };

    return [...accountsList].sort((left, right) => {
      const rankDiff = rank(left.name) - rank(right.name);
      if (rankDiff !== 0) {
        return rankDiff;
      }
      return String(left.name || '').localeCompare(String(right.name || ''));
    });
  };

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const getStatementMonthForCredit = (credit) => resolveStatementMonthForDate(new Date(), credit?.statement_start_day || 1);

  const handleCreditSelection = (value) => {
    setCreditId(value);
    const selectedCredit = credits.find((credit) => String(credit.id) === String(value));
    if (!selectedCredit) {
      return;
    }
    setStatementMonth(getStatementMonthForCredit(selectedCredit));
  };

  const parseMonthToShort = (value) => {
    if (!value) {
      return null;
    }

    const cleaned = String(value).trim().toLowerCase();
    if (!cleaned) {
      return null;
    }

    if (/^\d+$/.test(cleaned)) {
      const monthNum = parseInt(cleaned, 10);
      if (monthNum < 1 || monthNum > 12) {
        return null;
      }
      return new Date(2000, monthNum - 1, 1).toLocaleDateString('en-US', { month: 'short' });
    }

    const monthMap = {
      jan: 'Jan',
      feb: 'Feb',
      mar: 'Mar',
      apr: 'Apr',
      may: 'May',
      jun: 'Jun',
      jul: 'Jul',
      aug: 'Aug',
      sep: 'Sep',
      sept: 'Sep',
      oct: 'Oct',
      nov: 'Nov',
      dec: 'Dec'
    };

    return monthMap[cleaned.slice(0, 4)] || monthMap[cleaned.slice(0, 3)] || null;
  };

  const buildImportPreview = async (file) => {
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0);
    if (lines.length <= 1) {
      return { total: 0, months: [] };
    }

    const headers = lines[0].split(',').map((header) => header.trim().replace(/^"|"$/g, ''));
    const monthIndex = headers.findIndex((header) => header.toLowerCase() === 'month');
    if (monthIndex < 0) {
      return { total: 0, months: [] };
    }

    const monthCounts = {};
    let total = 0;

    lines.slice(1).forEach((line) => {
      const columns = line.split(',').map((column) => column.trim().replace(/^"|"$/g, ''));
      const monthName = parseMonthToShort(columns[monthIndex]);
      if (!monthName) {
        return;
      }
      total += 1;
      monthCounts[monthName] = (monthCounts[monthName] || 0) + 1;
    });

    const orderedMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const months = orderedMonths
      .filter((month) => monthCounts[month])
      .map((month) => ({ month, count: monthCounts[month] }));

    return { total, months };
  };

  const loadTransactions = async (token) => {
    const response = await fetch('/api/accounting/transactions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ user, statement_month: filterMonth || undefined, limit: 5000 })
    });

    if (!response.ok) {
      throw new Error('Failed to load transactions');
    }

    return response.json();
  };

  const loadCredits = async (token) => {
    const response = await fetch('/api/accounting/credits', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ user })
    });

    if (!response.ok) {
      throw new Error('Failed to load credits');
    }

    return response.json();
  };

  const loadAccounts = async (token) => {
    const response = await fetch('/api/accounting/accounts', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ user })
    });

    if (!response.ok) {
      throw new Error('Failed to load accounts');
    }

    return response.json();
  };

  const loadExpenses = async (token) => {
    const response = await fetch('/api/accounting/expenses/for-month', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ user, month: filterMonth })
    });

    if (!response.ok) {
      throw new Error('Failed to load expenses');
    }

    return response.json();
  };

  const refreshTransactions = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }

    try {
      const [transactionData, creditData, accountsData, expensesData] = await Promise.all([
        loadTransactions(token),
        loadCredits(token),
        loadAccounts(token),
        loadExpenses(token)
      ]);
      const creditsList = Array.isArray(creditData) ? creditData : [];
      setCredits(creditsList);
      let nextCreditId = creditId;
      if (!creditId && creditsList.length > 0) {
        nextCreditId = getPreferredCreditId(creditsList);
        setCreditId(nextCreditId);
      }

      if (!hasInitializedCreditCycleMonth && creditsList.length > 0) {
        const activeCredit = creditsList.find((credit) => String(credit.id) === String(nextCreditId))
          || creditsList.find((credit) => String(credit.name || '').trim().toLowerCase() === 'barclaycard')
          || creditsList[0];
        if (activeCredit) {
          const resolvedMonth = getStatementMonthForCredit(activeCredit);
          setFilterMonth(resolvedMonth);
          setStatementMonth(resolvedMonth);
          setHasInitializedCreditCycleMonth(true);
        }
      }

      const accountsList = sortAccountsByPreference(Array.isArray(accountsData) ? accountsData : []);
      setAccounts(accountsList);
      if (!accountId && accountsList.length > 0) {
        setAccountId(String(accountsList[0].id));
      }

      const data = transactionData;
      setTransactions(Array.isArray(data) ? data : []);
      setExpenses(Array.isArray(expensesData) ? expensesData : []);
    } catch (error) {
      setStatusMessage('Unable to load transactions.');
    }
  };

  useEffect(() => {
    refreshTransactions();
  }, [filterMonth]);

  useEffect(() => {
    setHiddenForecastAccountsState(getHiddenForecastAccounts(user));
  }, [user]);

  useEffect(() => {
    if (filterMonth) {
      setStatementMonth(filterMonth);
    }
  }, [filterMonth]);

  useEffect(() => {
    if (entryType === 'debit' && !accountId && accounts.length > 0) {
      setAccountId(String(accounts[0].id));
    }
    if (entryType === 'credit' && !creditId && credits.length > 0) {
      const preferredId = getPreferredCreditId(credits);
      handleCreditSelection(preferredId);
    }
  }, [entryType, accounts, credits, accountId, creditId]);

  const handleAddTransaction = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }

    try {
      const selectedCredit = credits.find((credit) => String(credit.id) === String(creditId));
      const selectedAccount = accounts.find((account) => String(account.id) === String(accountId));
      const response = await fetch('/api/accounting/transactions/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user,
          entry_type: entryType,
          amount,
          description,
          source: entryType === 'debit' ? (selectedAccount ? selectedAccount.name : '') : (selectedCredit ? selectedCredit.name : ''),
          credit_id: entryType === 'credit' ? (creditId || null) : null,
          account_id: entryType === 'debit' ? (accountId || null) : null,
          statement_month: statementMonth || null
        })
      });

      if (response.ok) {
        setAmount('');
        setDescription('');
        setStatusMessage('Transaction logged.');
        refreshTransactions();
      } else {
        setStatusMessage('Unable to log transaction.');
      }
    } catch (error) {
      setStatusMessage('Unable to log transaction.');
    }
  };

  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setEntryType(transaction.entry_type);
    setAmount(String(transaction.amount));
    setDescription(transaction.description || '');
    setStatementMonth(transaction.statement_month || new Date().toISOString().slice(0, 7));
    if (transaction.entry_type === 'credit') {
      setCreditId(String(transaction.credit_id || ''));
      setAccountId('');
    } else {
      setAccountId(String(transaction.account_id || ''));
      setCreditId('');
    }
    setIsEditModalOpen(true);
  };

  const handleUpdateTransaction = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token || !editingTransaction) {
      return;
    }

    try {
      const response = await fetch('/api/accounting/transactions/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user,
          transaction_id: editingTransaction.id,
          entry_type: entryType,
          amount,
          description,
          credit_id: entryType === 'credit' ? (creditId || null) : null,
          account_id: entryType === 'debit' ? (accountId || null) : null,
          statement_month: statementMonth || null
        })
      });

      if (response.ok) {
        setAmount('');
        setDescription('');
        setIsEditModalOpen(false);
        setEditingTransaction(null);
        setStatusMessage('Transaction updated.');
        refreshTransactions();
      } else {
        setStatusMessage('Unable to update transaction.');
      }
    } catch (error) {
      setStatusMessage('Unable to update transaction.');
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }

    try {
      const response = await fetch('/api/accounting/transactions/delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ user, transaction_id: transactionId })
      });

      if (response.ok) {
        refreshTransactions();
      }
    } catch (error) {
      console.error('Unable to delete transaction', error);
    }
  };

  const handleEditExpenseForMonth = (expense) => {
    setEditingExpense(expense);
    setIsEditExpenseModalOpen(true);
  };

  const handleCreateExpenseOverride = async (overrideData) => {
    const token = localStorage.getItem('accessToken');
    if (!token || !editingExpense) {
      return;
    }

    try {
      const response = await fetch('/api/accounting/expenses/create-month-override', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user,
          base_expense_id: editingExpense.id,
          month: filterMonth,
          name: overrideData.name,
          amount: overrideData.amount,
          category: overrideData.category,
          transfer_account_id: overrideData.transfer_account_id,
          source_account_id: overrideData.source_account_id,
          credit_id: overrideData.credit_id
        })
      });

      if (response.ok) {
        setIsEditExpenseModalOpen(false);
        setEditingExpense(null);
        setStatusMessage('Month-specific expense override created.');
        refreshTransactions();
      } else {
        const error = await response.json();
        setStatusMessage(error.error || 'Unable to create expense override.');
      }
    } catch (error) {
      setStatusMessage('Unable to create expense override.');
    }
  };

  const handleBulkDeleteMonth = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }

    const monthName = new Date(deleteYear, deleteMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    if (!window.confirm(`Delete all transactions for ${monthName}? This cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch('/api/accounting/transactions/delete/month', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ user, year: parseInt(deleteYear), month: parseInt(deleteMonth) })
      });

      const data = await response.json();
      if (response.ok) {
        setDeleteStatus(`Deleted ${data.deleted_count} transactions.`);
        refreshTransactions();
      } else {
        setDeleteStatus(data?.error || 'Unable to delete transactions.');
      }
    } catch (error) {
      setDeleteStatus('Unable to delete transactions.');
    }
  };

  const handleImportTransactions = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }
    if (!importFile) {
      setImportStatus('Pick a CSV file to import.');
      return;
    }

    setIsImporting(true);
    setImportProgress('Preparing import...');
    setImportTotal(0);
    setImportDetails([]);

    try {
      const preview = await buildImportPreview(importFile);
      setImportTotal(preview.total);
      setImportDetails(preview.months);
      setImportProgress(`Found total transactions: ${preview.total}`);

      for (const monthData of preview.months) {
        setImportProgress(`Found ${monthData.count} transactions for ${monthData.month}`);
        await delay(220);
      }

      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('user', user);
      formData.append('year', importYear);
      formData.append('entry_type', importEntryType);

      setImportProgress('Uploading file...');
      const importRequest = fetch('/api/accounting/import/transactions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      for (const monthData of preview.months) {
        setImportProgress(`Adding ${monthData.count} transactions for ${monthData.month}...`);
        await delay(260);
      }

      setImportProgress('Finalizing import...');
      const response = await importRequest;

      if (!response.ok) {
        const data = await response.json();
        setImportStatus(data?.error || 'Unable to import transactions.');
        return;
      }

      const data = await response.json();
      setImportProgress('Done');
      setImportStatus(`Imported ${data.imported} transactions (${data.skipped} skipped).`);
      setImportFile(null);
      await delay(350);
      refreshTransactions();
    } catch (error) {
      setImportStatus('Unable to import transactions.');
      console.error('Import error:', error);
    } finally {
      setIsImporting(false);
    }
  };

  const filteredTransactions = useMemo(() => {
    return transactions.filter((transaction) => filterType === 'all' || transaction.entry_type === filterType);
  }, [transactions, filterType]);

  const canQuickAdd = Number(amount) > 0 && (entryType === 'credit' ? Boolean(creditId) : Boolean(accountId));

  const hiddenForecastAccountSet = useMemo(() => {
    return new Set(hiddenForecastAccounts);
  }, [hiddenForecastAccounts]);

  const handleForecastVisibilityToggle = (accountName) => {
    const normalized = normalizeForecastAccountName(accountName);
    if (!normalized) {
      return;
    }

    setHiddenForecastAccountsState((previous) => {
      const set = new Set(previous);
      if (set.has(normalized)) {
        set.delete(normalized);
      } else {
        set.add(normalized);
      }
      const next = Array.from(set);
      setHiddenForecastAccounts(user, next);
      return next;
    });
  };

  const handleShowAllForecastAccounts = () => {
    setHiddenForecastAccountsState([]);
    setHiddenForecastAccounts(user, []);
  };

  return (
    <div className="lib-page">
      <section className="accounting-page">
        <div className="section-header">
          <div>
            <h2>Transactions</h2>
            <p className="section-subtitle">Log transactions quickly and see balances in one place.</p>
          </div>
          <div className="table-actions">
            <button className="login-button" type="button" onClick={() => setIsQuickAddOpen((prev) => !prev)}>
              {isQuickAddOpen ? 'Hide quick add' : 'Quick add'}
            </button>
            <button className="login-button" type="button" onClick={() => setIsAdvancedOpen((prev) => !prev)}>
              {isAdvancedOpen ? 'Hide advanced features' : 'Show advanced features'}
            </button>
          </div>
        </div>

        {isQuickAddOpen && (
          <section className="panel">
            <div className="section-header">
              <div>
                <h3>Quick Add Transaction</h3>
                <p className="panel-sub">Fast path: amount, account, save.</p>
              </div>
            </div>
            <form
              className="form-grid"
              onSubmit={(e) => {
                e.preventDefault();
                handleAddTransaction();
              }}
            >
              <div className="form-row">
                <label>
                  Type
                  <select value={entryType} onChange={(e) => setEntryType(e.target.value)}>
                    <option value="debit">Debit</option>
                    <option value="credit">Credit</option>
                  </select>
                </label>
                <label>
                  Amount
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                  />
                </label>
                <label>
                  Description
                  <input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Optional"
                  />
                </label>
                {entryType === 'credit' ? (
                  <label>
                    Credit account
                    <select value={creditId} onChange={(e) => handleCreditSelection(e.target.value)}>
                      <option value="">Select credit</option>
                      {credits.map((credit) => (
                        <option key={credit.id} value={credit.id}>{credit.name}</option>
                      ))}
                    </select>
                  </label>
                ) : (
                  <label>
                    Account
                    <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                      <option value="">Select account</option>
                      {accounts.map((account) => (
                        <option key={account.id} value={account.id}>{account.name}</option>
                      ))}
                    </select>
                  </label>
                )}
                <label>
                  Month
                  <input type="month" value={statementMonth} onChange={(e) => setStatementMonth(e.target.value)} />
                </label>
              </div>
              <div className="form-row">
                <button className="login-button" type="submit" disabled={!canQuickAdd}>Save transaction</button>
              </div>
            </form>
          </section>
        )}

        <section className="panel">
          <div className="section-header">
            <div>
              <h3>Recent Activity</h3>
              <p className="panel-sub">All transactions for the selected month in a scrollable list.</p>
            </div>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <label>
                Type
                <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                  <option value="all">All</option>
                  <option value="debit">Debit</option>
                  <option value="credit">Credit</option>
                </select>
              </label>
              <label>
                Month
                <input type="month" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} />
              </label>
            </div>
          </div>
          {filteredTransactions.length === 0 ? (
            <div className="empty-state">No transactions match the current filters.</div>
          ) : (
            <div className="table-scroll transactions-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Month</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Description</th>
                    <th>Source</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((row) => (
                    <tr key={row.id}>
                      <td>{new Date(row.occurred_at * 1000).toLocaleDateString()}</td>
                      <td>{row.statement_month || '-'}</td>
                      <td>{row.entry_type}</td>
                      <td>{formatCurrency(row.amount)}</td>
                      <td>{row.description || '-'}</td>
                      <td>{row.source || '-'}</td>
                      <td>
                        <button className="login-button" type="button" onClick={() => handleEditTransaction(row)} style={{ marginRight: '0.5rem' }}>
                          Edit
                        </button>
                        <button className="login-button" type="button" onClick={() => handleDeleteTransaction(row.id)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="panel">
          <div className="section-header">
            <div>
              <h3>Monthly Expenses</h3>
              <p className="panel-sub">Expenses applicable to the selected month (recurring + month-specific).</p>
            </div>
          </div>
          {expenses.length === 0 ? (
            <div className="empty-state">No expenses for this month.</div>
          ) : (
            <div className="table-scroll transactions-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Amount</th>
                    <th>Category</th>
                    <th>Type</th>
                    <th>Month</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map((expense) => (
                    <tr key={expense.id}>
                      <td>{expense.name}</td>
                      <td>{formatCurrency(expense.amount)}</td>
                      <td>{expense.category || '-'}</td>
                      <td>{expense.recurring ? 'Recurring' : 'One-time'}</td>
                      <td>{expense.month || 'All months'}</td>
                      <td>
                        {expense.recurring && (
                          <button 
                            className="login-button" 
                            type="button" 
                            onClick={() => handleEditExpenseForMonth(expense)}
                          >
                            Edit for this month
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {isAdvancedOpen && (
          <div className="panel-grid">
            <section className="panel">
              <h3>Bulk Delete by Month</h3>
              <p className="panel-sub">Delete all transactions for a specific month.</p>
              <div className="form-grid">
                <div className="form-row">
                  <label>
                    Year
                    <input type="number" value={deleteYear} onChange={(e) => setDeleteYear(e.target.value)} />
                  </label>
                  <label>
                    Month
                    <select value={deleteMonth} onChange={(e) => setDeleteMonth(e.target.value)}>
                      <option value="1">January</option>
                      <option value="2">February</option>
                      <option value="3">March</option>
                      <option value="4">April</option>
                      <option value="5">May</option>
                      <option value="6">June</option>
                      <option value="7">July</option>
                      <option value="8">August</option>
                      <option value="9">September</option>
                      <option value="10">October</option>
                      <option value="11">November</option>
                      <option value="12">December</option>
                    </select>
                  </label>
                </div>
                <div className="form-row">
                  <button className="login-button danger-button" type="button" onClick={handleBulkDeleteMonth}>
                    Delete all transactions for month
                  </button>
                </div>
                {deleteStatus && <span className="section-subtitle">{deleteStatus}</span>}
              </div>
            </section>

            <section className="panel">
              <h3>Import Transactions</h3>
              <p className="panel-sub">Upload a CSV with columns: Expense, Barclaycard, Paypal, Extra Info, Month (or legacy format).</p>
              <div className="form-grid">
                <div className="form-row">
                  <label>
                    CSV file
                    <input type="file" accept=".csv" onChange={(e) => setImportFile(e.target.files[0] || null)} />
                  </label>
                  <label>
                    Year
                    <input type="number" value={importYear} onChange={(e) => setImportYear(e.target.value)} />
                  </label>
                  <label>
                    Imported type
                    <select value={importEntryType} onChange={(e) => setImportEntryType(e.target.value)}>
                      <option value="credit">Credit</option>
                      <option value="debit">Debit</option>
                    </select>
                  </label>
                </div>
                <div className="form-row">
                  <button className="login-button" type="button" onClick={handleImportTransactions}>Import CSV</button>
                </div>
                {importStatus && <span className="section-subtitle">{importStatus}</span>}
              </div>
            </section>

            <section className="panel">
              <h3>Account Forecast Visibility</h3>
              <p className="panel-sub">Hide selected accounts in Account Forecast tables across the app.</p>
              {accounts.length === 0 ? (
                <div className="empty-state">No accounts available.</div>
              ) : (
                <div className="form-grid">
                  {accounts.map((account) => {
                    const normalized = normalizeForecastAccountName(account.name);
                    return (
                      <label className="inline-switch" key={`forecast-visible-${account.id}`}>
                        <input
                          type="checkbox"
                          checked={!hiddenForecastAccountSet.has(normalized)}
                          onChange={() => handleForecastVisibilityToggle(account.name)}
                        />
                        <span>{account.name}</span>
                      </label>
                    );
                  })}
                  <div className="form-row">
                    <button className="login-button" type="button" onClick={handleShowAllForecastAccounts}>
                      Show all accounts in forecast
                    </button>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}

        {statusMessage && <span className="section-subtitle">{statusMessage}</span>}
      </section>

      <TransactionModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingTransaction(null);
        }}
        onSave={handleUpdateTransaction}
        entryType={entryType}
        setEntryType={setEntryType}
        amount={amount}
        setAmount={setAmount}
        description={description}
        setDescription={setDescription}
        creditId={creditId}
        setCreditId={handleCreditSelection}
        accountId={accountId}
        setAccountId={setAccountId}
        statementMonth={statementMonth}
        setStatementMonth={setStatementMonth}
        credits={credits}
        accounts={accounts}
        statusMessage={statusMessage}
        isEditMode={true}
      />

      {isEditExpenseModalOpen && editingExpense && (
        <div className="modal-overlay" onClick={() => setIsEditExpenseModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Edit {editingExpense.name} for {filterMonth}</h3>
            <p style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#666' }}>
              This will create a one-time override for this specific month without changing the recurring expense.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                handleCreateExpenseOverride({
                  name: formData.get('name'),
                  amount: formData.get('amount'),
                  category: formData.get('category'),
                  transfer_account_id: formData.get('transfer_account_id') || null,
                  source_account_id: formData.get('source_account_id') || null,
                  credit_id: formData.get('credit_id') || null
                });
              }}
            >
              <div className="form-grid">
                <label>
                  Name
                  <input type="text" name="name" defaultValue={editingExpense.name} required />
                </label>
                <label>
                  Amount
                  <input type="number" step="0.01" name="amount" defaultValue={editingExpense.amount} required />
                </label>
                <label>
                  Category
                  <input type="text" name="category" defaultValue={editingExpense.category || ''} />
                </label>
                {editingExpense.transfer_account_id && (
                  <>
                    <label>
                      Transfer to Account
                      <select name="transfer_account_id" defaultValue={editingExpense.transfer_account_id || ''}>
                        <option value="">None</option>
                        {accounts.map((acc) => (
                          <option key={acc.id} value={acc.id}>
                            {acc.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Source Account
                      <select name="source_account_id" defaultValue={editingExpense.source_account_id || ''}>
                        <option value="">None</option>
                        {accounts.map((acc) => (
                          <option key={acc.id} value={acc.id}>
                            {acc.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  </>
                )}
                {editingExpense.credit_id && (
                  <label>
                    Credit Card
                    <select name="credit_id" defaultValue={editingExpense.credit_id || ''}>
                      <option value="">None</option>
                      {credits.map((credit) => (
                        <option key={credit.id} value={credit.id}>
                          {credit.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
              <div className="modal-actions">
                <button type="submit" className="login-button">Create Override</button>
                <button type="button" className="login-button" onClick={() => setIsEditExpenseModalOpen(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isImporting && (
        <div className="modal-overlay">
          <div className="progress-modal">
            <div className="spinner"></div>
            <h3>Importing Transactions</h3>
            <p>{importProgress}</p>
            {importTotal > 0 && (
              <div className="progress-details">
                <p className="progress-stat">Total rows: {importTotal}</p>
                {importDetails.length > 0 && (
                  <div className="progress-list">
                    {importDetails.map(detail => (
                      <div key={detail.month} className="progress-item">
                        {detail.month}: {detail.count} transaction{detail.count !== 1 ? 's' : ''}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <p className="progress-note">Please wait...</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Transactions;
